import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import {
  GuestRoute,
  OnboardingAuthRoute,
  RoleRoute,
  RootRedirect,
  ExternalRedirect,
} from 'components/RouteGuards';
import { RESOURCE_CENTER_URL } from 'constants/resourceCenterUrls';

import { Login } from './Auth/Login';
import { Register } from './Auth/Register/RegisterPage';
import { Invite } from './Auth/Invite/InvitePage';
import { ResetPassword, ResetForm } from './Auth/ResetPassword';
import { LostInvite } from './Auth/LostInvite';
import { Setup2FA } from './Auth/Setup2FA';
import { UpdatePassword } from './Auth/UpdatePassword';

import { Dashboard } from './Dashboard';
import { Buildings } from './Property/Buildings';
import { BuildingDetail } from './Property/Buildings/Details/BuildingDetail';
import { Applications } from './Property/Applications';
import { ApplicationDetail } from './Property/Applications/Details';
import { Policies } from './Property/Policies';
import { PolicyDetail } from './Property/Policies/Details';
import { FlaggedCancellations } from './Property/FlaggedCancellations';
import { FlaggedCancellationDetail } from './Property/FlaggedCancellations/Details';
import { Claims } from './Property/Claims';
import { ClaimDetail } from './Property/Claims/Details';
import { Invoice } from './Invoice';
import { InvoiceDetail } from './Invoice/details';
import { TreeviewBox } from './User/Treeview';
import { UserByRole } from './User/UserByRole';
import { UserDetail, UserEdit } from './User/UserByRole/UserDetail';
import { DetailBuilding } from './User/UserByRole/UserDetail/BuildingDetail';
import { InviteNewUser } from './User/InviteNewUser';
import { InviteUserDetail } from './User/InviteNewUser/Detail';
import { InvitePage } from './User/InviteNewUser/InvitePage';
import { CompanyInvite } from './LandlordInvite';
import { InviteCompanyDetail } from './LandlordInvite/Detail';
import { Landlord } from './User/Landlord';
import { LandlordDetail } from './User/Landlord/LandlordDetail';
import { Email } from './Email';
import { NotificationsPage } from './Notification';
import { NotificationDetail } from './Notification/NotificationDetail';
import { PolicyCancelLog } from './PolicyCancelLog';
import { PortalGuide } from './PortalGuide';
import { ActivityLog } from './ActivityLog';
import { DelegationInvitePage } from './Delegation/Invite';
import { DelegationRequestPage } from './Delegation/Request';
import { NotFound } from './NotFound';
import { Forbidden } from './Forbidden';
import { EmailDetail } from './Email/Detail';
import { EmailBuildingDetail } from './Email/Detail/BuildingDetail';
import { isFlaggedCancellationsUiVisible } from 'utils/flaggedCancellationUi';

function LegacyNotificationToDetailRedirect() {
  const { requestId } = useParams();
  return <Navigate to={`/notification/outgoing/${requestId}/detail`} replace />;
}

function LegacyNotificationFromDetailRedirect() {
  const { requestId } = useParams();
  return <Navigate to={`/notification/received/${requestId}/detail`} replace />;
}

function LegacyNotificationDetailRedirect() {
  const { requestId } = useParams();
  return <Navigate to={`/notification/outgoing/${requestId}/detail`} replace />;
}

function LegacyUserCompanyDetailRedirect() {
  const { landlordId } = useParams();
  return <Navigate to={`/user/operator/${landlordId}/detail/report`} replace />;
}

function LegacyUserCompanyDetailTabRedirect() {
  const { landlordId, tab } = useParams();
  return <Navigate to={`/user/operator/${landlordId}/detail/${tab}`} replace />;
}

// Temporary compatibility redirect for old bookmarked links.
function LegacyLandlordInviteDetailRedirect() {
  const { landlordId } = useParams();
  return <Navigate to={`/operator-invite/${landlordId}/detail/report`} replace />;
}

function LegacyLandlordInviteDetailTabRedirect() {
  const { landlordId, tab } = useParams();
  return <Navigate to={`/operator-invite/${landlordId}/detail/${tab}`} replace />;
}

function PropertyDetailTabRedirect() {
  const { buildingId } = useParams();
  return <Navigate to={`/property/properties/${buildingId}/detail/report`} replace />;
}

function InvoiceDetailTabRedirect() {
  const { invoiceId } = useParams();
  return <Navigate to={`/invoice/${invoiceId}/detail/current`} replace />;
}

function LandlordDetailTabRedirect() {
  const { landlordId } = useParams();
  return <Navigate to={`/user/operator/${landlordId}/detail/report`} replace />;
}

function InviteUserDetailTabRedirect() {
  const { landlordId } = useParams();
  return <Navigate to={`/user/invite-new-user/${landlordId}/detail/report`} replace />;
}

function InviteCompanyDetailTabRedirect() {
  const { landlordId } = useParams();
  return <Navigate to={`/operator-invite/${landlordId}/detail/report`} replace />;
}

function findPropertyDetailBasePath(pathname, buildingId) {
  for (const segment of ['propertyDetail', 'buildingDetail']) {
    const marker = `/${segment}/${buildingId}`;
    const idx = pathname.indexOf(marker);
    if (idx !== -1) {
      return pathname.slice(0, idx + marker.length);
    }
  }
  return null;
}

function UserPropertyDetailTabRedirect() {
  const { buildingId } = useParams();
  const { pathname } = useLocation();
  const base = findPropertyDetailBasePath(pathname, buildingId);
  if (!base) return <Navigate to="/user/property" replace />;
  if (base.includes('/buildingDetail/')) {
    return (
      <Navigate to={`${base.replace('/buildingDetail/', '/propertyDetail/')}/report`} replace />
    );
  }
  return <Navigate to={`${base}/report`} replace />;
}

function EmailPropertyDetailTabRedirect() {
  const { buildingId, emailId } = useParams();
  const { pathname } = useLocation();
  const base = findPropertyDetailBasePath(pathname, buildingId);
  if (!base) {
    return pathname.startsWith('/email/') ? (
      <Navigate to={`/email/${emailId}/detail`} replace />
    ) : (
      <Navigate to={`/invitations/${emailId}/detail`} replace />
    );
  }
  if (base.includes('/buildingDetail/')) {
    return (
      <Navigate to={`${base.replace('/buildingDetail/', '/propertyDetail/')}/report`} replace />
    );
  }
  return <Navigate to={`${base}/report`} replace />;
}

const AppRoutes = () => {
  const withRoles = (element, allowedRoles) => (
    <RoleRoute allowedRoles={allowedRoles}>{element}</RoleRoute>
  );

  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />

      {/** guest-only auth pages */}
      <Route
        exact
        path="/login"
        element={
          <GuestRoute>
            <Login />
          </GuestRoute>
        }
      />
      <Route path="/reset-password">
        <Route
          path=""
          element={
            <GuestRoute>
              <ResetPassword />
            </GuestRoute>
          }
        />
        <Route
          path=":token"
          element={
            <GuestRoute>
              <ResetForm />
            </GuestRoute>
          }
        />
      </Route>

      <Route
        exact
        path="/lost-invite"
        element={
          <GuestRoute>
            <LostInvite />
          </GuestRoute>
        }
      />
      <Route
        exact
        path="/register"
        element={
          <GuestRoute>
            <Register />
          </GuestRoute>
        }
      />
      <Route
        exact
        path="/invite/:type/:token"
        element={
          <GuestRoute>
            <Invite />
          </GuestRoute>
        }
      />
      <Route
        exact
        path="/setup-2fa"
        element={
          <OnboardingAuthRoute flow="setup2fa">
            <Setup2FA />
          </OnboardingAuthRoute>
        }
      />
      <Route
        exact
        path="/update-auth"
        element={
          <OnboardingAuthRoute flow="updatePassword">
            <UpdatePassword />
          </OnboardingAuthRoute>
        }
      />

      <Route exact path="/dashboard" element={<Dashboard />} />
      <Route exact path="/dashboard/invoice" element={<Navigate to="/invoice" replace />} />
      {/* Legacy production bookmarks → redesign routes */}
      <Route
        exact
        path="/dashboard/property/buildings"
        element={<Navigate to="/property/properties" replace />}
      />
      <Route
        exact
        path="/dashboard/property/applications"
        element={<Navigate to="/property/applications" replace />}
      />
      <Route
        exact
        path="/dashboard/property/policies"
        element={<Navigate to="/property/policies" replace />}
      />
      <Route
        exact
        path="/dashboard/property/claims"
        element={<Navigate to="/property/claims" replace />}
      />
      <Route
        exact
        path="/dashboard/property/cancel-policies"
        element={
          isFlaggedCancellationsUiVisible() ? (
            <Navigate to="/property/cancel-policies" replace />
          ) : (
            <Navigate to="/property/properties" replace />
          )
        }
      />
      <Route
        exact
        path="/dashboard/user/landlord"
        element={<Navigate to="/user/operator" replace />}
      />
      <Route
        exact
        path="/dashboard/user/company"
        element={<Navigate to="/user/operator" replace />}
      />
      <Route
        exact
        path="/dashboard/user/full-portfolio"
        element={<Navigate to="/user/full-portfolio" replace />}
      />
      <Route
        exact
        path="/dashboard/user/multi-site"
        element={<Navigate to="/user/multi-site" replace />}
      />
      <Route
        exact
        path="/dashboard/user/property"
        element={<Navigate to="/user/property" replace />}
      />
      <Route
        exact
        path="/dashboard/user/treeview"
        element={<Navigate to="/user/treeview" replace />}
      />
      <Route
        exact
        path="/dashboard/invite-user"
        element={<Navigate to="/user/invite-new-user" replace />}
      />
      <Route
        exact
        path="/dashboard/landlord-invite"
        element={<Navigate to="/operator-invite" replace />}
      />
      <Route
        exact
        path="/dashboard/company-invite"
        element={<Navigate to="/operator-invite" replace />}
      />
      <Route exact path="/dashboard/invite" element={<Navigate to="/invite" replace />} />
      <Route exact path="/dashboard/request" element={<Navigate to="/request" replace />} />
      <Route exact path="/dashboard/email" element={<Navigate to="/invitations" replace />} />
      <Route
        exact
        path="/dashboard/notification/to"
        element={<Navigate to="/notification?tab=outgoing" replace />}
      />
      <Route
        exact
        path="/dashboard/notification/from"
        element={<Navigate to="/notification?tab=received" replace />}
      />
      <Route path="/resource" element={<ExternalRedirect to={RESOURCE_CENTER_URL} />} />
      <Route path="/resource-center" element={<ExternalRedirect to={RESOURCE_CENTER_URL} />} />
      <Route path="/dashboard/resource" element={<ExternalRedirect to={RESOURCE_CENTER_URL} />} />

      {/** property pages */}
      <Route path="/property">
        <Route exact path="properties" element={<Buildings />} />
        <Route exact path="properties/:buildingId/detail" element={<PropertyDetailTabRedirect />} />
        <Route exact path="properties/:buildingId/detail/:tab" element={<BuildingDetail />} />
        <Route exact path="buildings" element={<Navigate to="/property/properties" replace />} />
        <Route exact path="buildings/:buildingId/detail" element={<PropertyDetailTabRedirect />} />
        <Route exact path="buildings/:buildingId/detail/:tab" element={<BuildingDetail />} />
        <Route exact path="applications" element={<Applications />} />
        <Route exact path="applications/:applicationId/detail" element={<ApplicationDetail />} />
        <Route exact path="policies" element={<Policies />} />
        <Route exact path="policies/:policyId/detail" element={<PolicyDetail />} />
        <Route
          exact
          path="cancel-policies"
          element={
            isFlaggedCancellationsUiVisible() ? (
              <FlaggedCancellations />
            ) : (
              <Navigate to="/property/properties" replace />
            )
          }
        />
        <Route
          exact
          path="cancel-policies/:policyId/detail"
          element={
            isFlaggedCancellationsUiVisible() ? (
              <FlaggedCancellationDetail />
            ) : (
              <Navigate to="/property/properties" replace />
            )
          }
        />
        <Route exact path="claims" element={<Claims />} />
        <Route exact path="claims/:claimId/detail" element={<ClaimDetail />} />
      </Route>

      <Route exact path="/invoice" element={<Invoice />} />
      <Route exact path="/invoice/:invoiceId/detail" element={<InvoiceDetailTabRedirect />} />
      <Route exact path="/invoice/:invoiceId/detail/:tab" element={<InvoiceDetail />} />

      {/** user pages */}
      <Route path="/user">
        <Route exact path="treeview" element={withRoles(<TreeviewBox />, ['admin', 'll', 'vp'])} />
        <Route exact path="operator" element={withRoles(<Landlord />, ['admin'])} />
        <Route
          exact
          path="operator/:landlordId/detail"
          element={withRoles(<LandlordDetailTabRedirect />, ['admin'])}
        />
        <Route
          exact
          path="operator/:landlordId/detail/:tab"
          element={withRoles(<LandlordDetail />, ['admin'])}
        />
        <Route exact path="company" element={<Navigate to="/user/operator" replace />} />
        <Route
          exact
          path="company/:landlordId/detail"
          element={<LegacyUserCompanyDetailRedirect />}
        />
        <Route
          exact
          path="company/:landlordId/detail/:tab"
          element={<LegacyUserCompanyDetailTabRedirect />}
        />
        <Route exact path="landlord" element={<Navigate to="/user/operator" replace />} />
        <Route
          exact
          path="landlord/:landlordId/detail"
          element={<LegacyUserCompanyDetailRedirect />}
        />
        <Route
          exact
          path="full-portfolio"
          element={withRoles(
            <UserByRole role="vp" title="Full Portfolio" route="full-portfolio" />,
            ['admin', 'll'],
          )}
        />
        <Route
          exact
          path="multi-site"
          element={withRoles(<UserByRole role="rm" title="Multi-Site" route="multi-site" />, [
            'admin',
            'll',
            'vp',
          ])}
        />
        <Route
          exact
          path="property"
          element={withRoles(<UserByRole role="pm" title="Property Manager" route="property" />, [
            'admin',
            'll',
            'vp',
            'rm',
          ])}
        />

        <Route
          exact
          path="full-portfolio/:delegationId/detail"
          element={withRoles(<UserDetail route="full-portfolio" />, ['admin', 'll'])}
        />
        <Route
          exact
          path="multi-site/:delegationId/detail"
          element={withRoles(<UserDetail route="multi-site" />, ['admin', 'll', 'vp'])}
        />
        <Route
          exact
          path="property/:delegationId/detail"
          element={withRoles(<UserDetail route="property" />, ['admin', 'll', 'vp', 'rm'])}
        />

        <Route
          exact
          path="full-portfolio/:delegationId/edit"
          element={withRoles(<UserEdit role="vp" title="Full Portfolio" route="full-portfolio" />, [
            'admin',
            'll',
          ])}
        />
        <Route
          exact
          path="multi-site/:delegationId/edit"
          element={withRoles(<UserEdit role="rm" title="Multi-Site" route="multi-site" />, [
            'admin',
            'll',
            'vp',
          ])}
        />
        <Route
          exact
          path="property/:delegationId/edit"
          element={withRoles(<UserEdit role="pm" title="Property Manager" route="property" />, [
            'admin',
            'll',
            'vp',
            'rm',
          ])}
        />

        <Route
          exact
          path="full-portfolio/detail/propertyDetail/:buildingId"
          element={withRoles(<UserPropertyDetailTabRedirect />, ['admin', 'll'])}
        />
        <Route
          exact
          path="full-portfolio/detail/propertyDetail/:buildingId/:tab"
          element={withRoles(<DetailBuilding />, ['admin', 'll'])}
        />
        <Route
          exact
          path="full-portfolio/detail/buildingDetail/:buildingId"
          element={withRoles(<UserPropertyDetailTabRedirect />, ['admin', 'll'])}
        />
        <Route
          exact
          path="full-portfolio/detail/buildingDetail/:buildingId/:tab"
          element={withRoles(<DetailBuilding />, ['admin', 'll'])}
        />
        <Route
          exact
          path="multi-site/detail/propertyDetail/:buildingId"
          element={withRoles(<UserPropertyDetailTabRedirect />, ['admin', 'll', 'vp'])}
        />
        <Route
          exact
          path="multi-site/detail/propertyDetail/:buildingId/:tab"
          element={withRoles(<DetailBuilding />, ['admin', 'll', 'vp'])}
        />
        <Route
          exact
          path="multi-site/detail/buildingDetail/:buildingId"
          element={withRoles(<UserPropertyDetailTabRedirect />, ['admin', 'll', 'vp'])}
        />
        <Route
          exact
          path="multi-site/detail/buildingDetail/:buildingId/:tab"
          element={withRoles(<DetailBuilding />, ['admin', 'll', 'vp'])}
        />
        <Route
          exact
          path="property/detail/propertyDetail/:buildingId"
          element={withRoles(<UserPropertyDetailTabRedirect />, ['admin', 'll', 'vp', 'rm'])}
        />
        <Route
          exact
          path="property/detail/propertyDetail/:buildingId/:tab"
          element={withRoles(<DetailBuilding />, ['admin', 'll', 'vp', 'rm'])}
        />
        <Route
          exact
          path="property/detail/buildingDetail/:buildingId"
          element={withRoles(<UserPropertyDetailTabRedirect />, ['admin', 'll', 'vp', 'rm'])}
        />
        <Route
          exact
          path="property/detail/buildingDetail/:buildingId/:tab"
          element={withRoles(<DetailBuilding />, ['admin', 'll', 'vp', 'rm'])}
        />
        <Route exact path="invite-new-user" element={withRoles(<InviteNewUser />, ['admin'])} />
        <Route
          exact
          path="invite-new-user/:landlordId/detail"
          element={withRoles(<InviteUserDetailTabRedirect />, ['admin'])}
        />
        <Route
          exact
          path="invite-new-user/:landlordId/detail/:tab"
          element={withRoles(<InviteUserDetail />, ['admin'])}
        />
        <Route
          exact
          path="invite-new-user/:landlordId/invite"
          element={withRoles(<InvitePage />, ['admin'])}
        />
      </Route>

      <Route exact path="/operator-invite" element={withRoles(<CompanyInvite />, ['admin'])} />
      <Route
        exact
        path="/operator-invite/:landlordId/detail"
        element={withRoles(<InviteCompanyDetailTabRedirect />, ['admin'])}
      />
      <Route
        exact
        path="/operator-invite/:landlordId/detail/:tab"
        element={withRoles(<InviteCompanyDetail />, ['admin'])}
      />
      <Route exact path="/company-invite" element={<Navigate to="/operator-invite" replace />} />
      <Route
        exact
        path="/company-invite/:landlordId/detail"
        element={<LegacyLandlordInviteDetailRedirect />}
      />
      <Route
        exact
        path="/company-invite/:landlordId/detail/:tab"
        element={<LegacyLandlordInviteDetailTabRedirect />}
      />
      <Route exact path="/landlord-invite" element={<Navigate to="/operator-invite" replace />} />
      <Route
        exact
        path="/landlord-invite/:landlordId/detail"
        element={<LegacyLandlordInviteDetailRedirect />}
      />
      <Route exact path="/invitations" element={<Email />} />
      <Route exact path="/invitations/:emailId/detail" element={<EmailDetail />} />
      <Route
        exact
        path="/invitations/:emailId/detail/propertyDetail/:buildingId"
        element={<EmailPropertyDetailTabRedirect />}
      />
      <Route
        exact
        path="/invitations/:emailId/detail/propertyDetail/:buildingId/:tab"
        element={<EmailBuildingDetail />}
      />
      <Route
        exact
        path="/invitations/:emailId/detail/buildingDetail/:buildingId"
        element={<EmailPropertyDetailTabRedirect />}
      />
      <Route
        exact
        path="/invitations/:emailId/detail/buildingDetail/:buildingId/:tab"
        element={<EmailBuildingDetail />}
      />
      <Route exact path="/email" element={<Email />} />
      <Route exact path="/email/:emailId/detail" element={<EmailDetail />} />
      <Route
        exact
        path="/email/:emailId/detail/propertyDetail/:buildingId"
        element={<EmailPropertyDetailTabRedirect />}
      />
      <Route
        exact
        path="/email/:emailId/detail/propertyDetail/:buildingId/:tab"
        element={<EmailBuildingDetail />}
      />
      <Route
        exact
        path="/email/:emailId/detail/buildingDetail/:buildingId"
        element={<EmailPropertyDetailTabRedirect />}
      />
      <Route
        exact
        path="/email/:emailId/detail/buildingDetail/:buildingId/:tab"
        element={<EmailBuildingDetail />}
      />

      {/** notifications — Received (/from API) + Outgoing (/to API) on one page */}
      <Route
        path="/notification/received/:requestId/detail"
        element={<NotificationDetail type="from" />}
      />
      <Route
        path="/notification/outgoing/:requestId/detail"
        element={<NotificationDetail type="to" />}
      />
      <Route
        path="/notification/outgoing"
        element={<Navigate to="/notification?tab=outgoing" replace />}
      />
      <Route
        path="/notification/from"
        element={<Navigate to="/notification?tab=received" replace />}
      />
      <Route
        path="/notification/to"
        element={<Navigate to="/notification?tab=outgoing" replace />}
      />
      <Route
        path="/notification/from/:requestId/detail"
        element={<LegacyNotificationFromDetailRedirect />}
      />
      <Route
        path="/notification/:requestId/detail"
        element={<LegacyNotificationDetailRedirect />}
      />
      <Route path="/notification" element={<NotificationsPage />} />
      <Route path="/notifications" element={<Navigate to="/notification" replace />} />
      <Route
        path="/notifications/:requestId/detail"
        element={<LegacyNotificationToDetailRedirect />}
      />
      <Route
        path="/notifications/from/:requestId/detail"
        element={<LegacyNotificationFromDetailRedirect />}
      />
      <Route
        path="/notification/to/:requestId/detail"
        element={<LegacyNotificationToDetailRedirect />}
      />

      <Route exact path="/cancellation-log" element={<PolicyCancelLog />} />

      {/** delegation — logged-in invite flow (not auth token /invite/:type/:token) */}
      <Route exact path="/invite" element={withRoles(<DelegationInvitePage />, ['ll', 'vp'])} />
      <Route exact path="/request" element={withRoles(<DelegationRequestPage />, ['vp'])} />

      <Route exact path="/guide" element={<PortalGuide />} />
      <Route exact path="/active-log" element={withRoles(<ActivityLog />, ['admin'])} />
      <Route exact path="/forbidden" element={<Forbidden />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
